/*
 * name-builder.js — フワッペン「おなまえでまとめてカート」
 *
 * 名前（例: ゆうと）を入れると、必要な文字ワッペンと合計金額を出し、1回でカートへ入れる部品。
 *  - 前半: 純粋関数（DOM を触らない）。node からも require できる（module.exports）。
 *  - 後半: 画面の部品（mount）。ブラウザでだけ動く。sections/name-builder.liquid の
 *          [data-nb-root] を見つけて自動で動き出す（テーマエディタの再描画にも追従）。
 *
 * 商品データの前提（2026-10-03 公開データで確認）
 *  - 1文字1商品・1バリエーション。題名は「あ-ひらがなワッペン(宇宙恐竜)」の形で「-」の前が文字。
 *  - 「長音符-…」＝「ー」、「濁音-…」＝ ゛×2枚＋゜×1枚 の3枚入りセット。
 *  - 必要な濁音セット数 = max(ceil(濁点の数 / 2), 半濁点の数)
 *  - 小さい「ぃ」「ぉ」の商品は無い。英字は大文字のみ。
 *
 * 書き方の決まり: 正規表現や文字列の不可視文字・結合文字は、必ず \uXXXX のエスケープで書く
 * （そのまま貼ると目に見えず、直す人が気づけないため）。
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.FuNameBuilder = api;
    api.autoMount();
  }
})(this, function () {
  'use strict';

  var DEFAULT_MAX_CHARS = 20;
  var DEFAULT_SHIPPING_YEN = 360;
  var DAKU_PER_SET = 2; // 1セットに ゛ が2枚
  var HANDAKU_PER_SET = 1; // 1セットに ゜ が1枚
  var DAKU_SET_LABEL = '濁音セット（゛2枚＋゜1枚入り）';

  var MARK_DAKU = 'daku';
  var MARK_HANDAKU = 'handaku';
  var DAKU_COMBINING = '\u3099'; // 結合用の濁点
  var HANDAKU_COMBINING = '\u309A'; // 結合用の半濁点
  var DAKU_SPACING = '゛'; // ゛（単独の濁点）
  var HANDAKU_SPACING = '゜'; // ゜（単独の半濁点）
  // ハート（2026-10-07 晃久さん「ハートあるのに嘘になる」）：色の無い ♡ ♥ は1つのキー「♡」にそろえ、
  // Liquid が別に渡すハートの商品（data の "k":"♡"）につなぐ。色は1つ（section の設定 heart_product）
  var HEART = '\u2661'; // ♡（色の無いハート＝設定のハート・既定はエンジェルピンク）
  var HEART_RE = /^[\u2661\u2665]$/; // ♡ ♥
  var VS16 = '\uFE0F';
  // 色の付いたハートの絵文字 → 同じ色のハート（2026-10-07 晃久さん「💙やと青いハートが出たりできる？」「色付きは色付きに」）。
  // 値＝キー（Liquid がハートの棚の題名の色名でキーを付けて渡す＝sections/name-builder.liquid）。
  // 合う色の無い絵文字（🤎 など）は今までどおり「ありません」。紺・パールホワイトに合う絵文字は無い
  var COLOR_HEART = {
    '\u2764': '\u2764', // ❤ → セクシーレッド
    '\uD83E\uDDE1': '\uD83E\uDDE1', // 🧡 → バイタルオレンジ
    '\uD83D\uDC9B': '\uD83D\uDC9B', // 💛 → サニービタミンイエロー
    '\uD83D\uDC9A': '\uD83D\uDC9A', // 💚 → オアシスグリーン
    '\uD83D\uDC99': '\uD83D\uDC99', // 💙 → ウルトラマリンブルー
    '\uD83E\uDE75': '\uD83E\uDE75', // 🩵 → パウダーブルー
    '\uD83D\uDC9C': '\uD83D\uDC9C', // 💜 → ミステリーパープル
    '\uD83D\uDDA4': '\uD83D\uDDA4', // 🖤 → ディープブラック
    '\uD83E\uDD0D': '\uD83E\uDD0D', // 🤍 → ピュアホワイト
    '\uD83E\uDE77': '\uD83E\uDE77', // 🩷 → エンジェルピンク
    '\uD83D\uDC97': '\uD83E\uDE77', // 💗 → エンジェルピンク
    '\uD83D\uDC96': '\uD83E\uDE77' // 💖 → エンジェルピンク
  };
  function isHeartKey(k) {
    if (k === HEART) return true;
    for (var c in COLOR_HEART) {
      if (Object.prototype.hasOwnProperty.call(COLOR_HEART, c) && COLOR_HEART[c] === k) return true;
    }
    return false;
  }

  // 使える1文字キー（正規化後）: ひらがな・長音・英大文字・数字・＆
  var KEY_RE = /^[ぁ-ゖーA-Z0-9&]$/;
  var HIRA_RE = /^[ぁ-ゖ]$/;
  var KANJI_RE = /[々〆〇㐀-䶿一-鿿豈-﫿]|[\uD840-\uD87F][\uDC00-\uDFFF]/;
  var DASH_RE = /^[\-‐-―−〜～~]$/;
  var COMBINING_RE = /\p{M}/u;
  // ゼロ幅の文字（U+200B〜U+200D・U+2060・U+FEFF）
  var ZERO_WIDTH_RE = /[\u200B-\u200D\u2060\uFEFF]/g;
  // 空白（半角・全角・改行など）とゼロ幅の文字。1文字ずつの判定用
  var IGNORABLE_RE = /^[\s\u200B-\u200D\u2060\uFEFF]$/;

  // ---------------------------------------------------------------
  // 文字の正規化
  // ---------------------------------------------------------------

  /** カタカナ1文字 → ひらがな（ァ〜ヶ）。それ以外はそのまま。 */
  function kataToHira(ch) {
    var c = ch.charCodeAt(0);
    if (ch.length === 1 && c >= 0x30a1 && c <= 0x30f6) {
      return String.fromCharCode(c - 0x60);
    }
    return ch;
  }

  /** 英小文字 → 大文字（ASCII のみ。ß 等は触らない）。 */
  function asciiUpper(ch) {
    return /^[a-z]$/.test(ch) ? ch.toUpperCase() : ch;
  }

  /** 1文字（基底文字）をキーの形にそろえる。 */
  function baseForm(ch) {
    return asciiUpper(kataToHira(ch));
  }

  /**
   * NFKC をかけてよい「幅の違い」だけの文字か。
   * 全角英数記号（U+FF01〜FF5E）・半角カナ（U+FF61〜FF9F）・単独の濁点半濁点（U+309B・U+309C）。
   * これ以外で NFKC により形が変わる字（①→1、Ⅳ→IV、㌢→センチ、ﬁ→fi、ゟ→より 等）は使えない文字にする。
   */
  function isWidthForm(ch) {
    var c = ch.codePointAt(0);
    return (c >= 0xff01 && c <= 0xff5e) || (c >= 0xff61 && c <= 0xff9f) || c === 0x309b || c === 0x309c;
  }

  /** 商品の題名の「-」より前を、対応表のキーにする。使えないなら null。 */
  function normalizeKey(prefix) {
    if (prefix == null) return null;
    var p = String(prefix).trim();
    if (p === '長音符') return 'ー';
    var s = p.normalize('NFKC').replace(/\s+/g, '');
    var out = '';
    Array.from(s).forEach(function (ch) {
      out += baseForm(ch);
    });
    out = out.normalize('NFC');
    var cps = Array.from(out);
    if (cps.length !== 1) return null;
    return KEY_RE.test(cps[0]) || isComposedKana(cps[0]) ? cps[0] : null;
  }

  /** が・ぱ のような濁音付きひらがな1文字か（将来「が」単体の商品が出たとき用）。 */
  function isComposedKana(ch) {
    var cps = Array.from(ch.normalize('NFD'));
    return cps.length === 2 && HIRA_RE.test(cps[0]) && (cps[1] === DAKU_COMBINING || cps[1] === HANDAKU_COMBINING);
  }

  function hasTag(p, tag) {
    var tags = p && p.tags;
    if (!tags) return false;
    if (typeof tags === 'string') tags = tags.split(',');
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).trim() === tag) return true;
    }
    return false;
  }

  function toYen(p) {
    if (p == null) return 0;
    if (typeof p.priceYen === 'number') return p.priceYen;
    if (p.price == null || p.price === '') return 0;
    var v = Number(p.price);
    if (!isFinite(v)) return 0;
    // Shopify の price は最小単位×100（JPY でも 330円 → 33000）
    return Math.round(v / 100);
  }

  /** お名前（カートの properties の値）：ゼロ幅の文字を除き、空白を1つにまとめて前後を落とす。 */
  function cleanName(input) {
    return String(input == null ? '' : input)
      .replace(ZERO_WIDTH_RE, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ---------------------------------------------------------------
  // 対応表（文字 → 商品）
  // ---------------------------------------------------------------

  /**
   * 商品の配列から対応表を作る。
   * products: [{ title, variantId, available, price(最小単位), image, url, q(在庫数|null), tags|hidden }]
   *   （Liquid から来るときは短い鍵 t/v/a/p/img/u/q/h）
   * 入れないもの: 余白用・hidden タグ・題名に「-」が無い・文字に対応しない・
   *   バリエーションが無い（variantId が空）・価格が 0 以下
   * 返り値: { chars: {キー: 商品}, daku: 商品|null, skipped: [題名], duplicates: [{key, kept, dropped}], total }
   */
  function buildCatalog(products) {
    var cat = { chars: {}, daku: null, skipped: [], duplicates: [], total: 0 };
    (products || []).forEach(function (raw) {
      if (!raw) return;
      cat.total += 1;
      var title = String(raw.title || raw.t || '');
      var hidden = raw.hidden === true || raw.h === true || hasTag(raw, 'hidden');
      if (/^余白/.test(title) || hidden) {
        cat.skipped.push(title);
        return;
      }
      var hk = raw.k || raw.key;
      if (hk && isHeartKey(hk)) {
        // ハートの商品（題名に「-」が無い・キーは Liquid が "k" で渡す。♡＝色の無いハート・ほかは色ごと）
        var hv = raw.variantId != null ? raw.variantId : raw.v;
        var hy = toYen(typeof raw.priceYen === 'number' ? raw : { price: raw.price != null ? raw.price : raw.p });
        if (hv == null || hv === '' || !(hy > 0)) {
          cat.skipped.push(title);
          return;
        }
        cat.chars[hk] = pickBetter(cat, hk, cat.chars[hk], {
          key: hk,
          title: title,
          variantId: hv,
          available: (raw.available != null ? raw.available : raw.a) !== false,
          priceYen: hy,
          image: raw.image || raw.img || null,
          url: raw.url || raw.u || null,
          q: typeof raw.q === 'number' ? raw.q : null
        });
        return;
      }
      var i = title.indexOf('-');
      if (i <= 0) {
        cat.skipped.push(title);
        return;
      }
      var variantId = raw.variantId != null ? raw.variantId : raw.v;
      var priceYen = toYen(typeof raw.priceYen === 'number' ? raw : { price: raw.price != null ? raw.price : raw.p });
      if (variantId == null || variantId === '' || !(priceYen > 0)) {
        // バリエーションが無い・価格が無い/0以下 → カートに入れられないので対応表に入れない
        cat.skipped.push(title);
        return;
      }
      var prefix = title.slice(0, i).trim();
      var item = {
        title: title,
        variantId: variantId,
        available: (raw.available != null ? raw.available : raw.a) !== false,
        priceYen: priceYen,
        image: raw.image || raw.img || null,
        url: raw.url || raw.u || null,
        q: typeof raw.q === 'number' ? raw.q : null
      };
      if (prefix === '濁音') {
        item.key = '濁音';
        cat.daku = pickBetter(cat, '濁音', cat.daku, item);
        return;
      }
      var key = normalizeKey(prefix);
      if (!key) {
        cat.skipped.push(title);
        return;
      }
      item.key = key;
      cat.chars[key] = pickBetter(cat, key, cat.chars[key], item);
    });
    return cat;
  }

  /** 同じ文字の商品が2つ以上あったら、在庫のある方・先に来た方を残す。 */
  function pickBetter(cat, key, current, next) {
    if (!current) return next;
    var keep = current;
    var drop = next;
    if (!current.available && next.available) {
      keep = next;
      drop = current;
    }
    cat.duplicates.push({ key: key, kept: keep.title, dropped: drop.title });
    return keep;
  }

  // ---------------------------------------------------------------
  // 入力の解析
  // ---------------------------------------------------------------

  function newUnit(base) {
    return { base: base, marks: [], extra: '', lone: false, foreign: false, compat: false, markConflict: false };
  }

  /**
   * 入力を「1文字ずつ」に分ける。
   *  - 空白・ゼロ幅の文字は無視
   *  - 全角英数・半角カナ・単独の濁点半濁点だけ NFKC で直す。ほかの互換文字（①・Ⅳ・㌢ 等）は使えない文字
   *  - 濁点・半濁点は直前の文字にくっつける。同じ種類の重複は1つに丸め、゛と゜の両方が付いたら使えない文字
   *  - 先頭や、くっつける相手が無い濁点・半濁点は使えない文字（lone）
   */
  function splitUnits(input) {
    var units = [];

    function addCp(cp) {
      var last = units.length ? units[units.length - 1] : null;
      if (HEART_RE.test(cp)) {
        units.push(newUnit(HEART));
        return;
      }
      if (Object.prototype.hasOwnProperty.call(COLOR_HEART, cp)) {
        units.push(newUnit(COLOR_HEART[cp]));
        return;
      }
      if (cp === VS16 && last && isHeartKey(last.base) && !last.foreign) return; // 絵文字のハートの後ろの異体字セレクタ
      var mark = null;
      if (cp === DAKU_COMBINING || cp === DAKU_SPACING) mark = MARK_DAKU;
      else if (cp === HANDAKU_COMBINING || cp === HANDAKU_SPACING) mark = MARK_HANDAKU;
      if (mark) {
        if (!last || last.lone) {
          var lone = newUnit(mark === MARK_DAKU ? DAKU_SPACING : HANDAKU_SPACING);
          lone.lone = true;
          units.push(lone);
          return;
        }
        if (last.marks.indexOf(mark) >= 0) return; // 同じ種類の重複は落とす
        if (last.marks.length) last.markConflict = true; // ゛と゜の両方
        last.marks.push(mark);
        return;
      }
      if (COMBINING_RE.test(cp)) {
        // ほかの結合文字（é のアクセント、絵文字の異体字セレクタ等）→ その文字は使えない扱い
        if (last) {
          last.extra += cp;
          last.foreign = true;
        } else {
          var f = newUnit(cp);
          f.foreign = true;
          units.push(f);
        }
        return;
      }
      units.push(newUnit(baseForm(cp)));
    }

    Array.from(String(input == null ? '' : input)).forEach(function (raw) {
      if (IGNORABLE_RE.test(raw)) return;
      if (isWidthForm(raw)) {
        Array.from(raw.normalize('NFKC').replace(/\s+/g, '').normalize('NFD')).forEach(addCp);
        return;
      }
      if (raw.normalize('NFKC') !== raw.normalize('NFC')) {
        var c = newUnit(raw);
        c.compat = true;
        units.push(c);
        return;
      }
      Array.from(raw.normalize('NFD')).forEach(addCp);
    });
    return units;
  }

  function markChars(marks) {
    return marks
      .map(function (m) {
        return m === MARK_DAKU ? DAKU_COMBINING : HANDAKU_COMBINING;
      })
      .join('');
  }

  function displayOf(u) {
    if (u.lone || u.compat) return u.base;
    return (u.base + markChars(u.marks) + u.extra).normalize('NFC');
  }

  function unusableReason(u) {
    if (u.lone) return 'lone_mark';
    if (u.markConflict) return 'mark_conflict';
    if (u.compat) return KANJI_RE.test(u.base) ? 'kanji' : 'compat';
    if (u.foreign) return 'other';
    var b = u.base;
    if (b === 'ぃ' || b === 'ぉ') return 'small_missing'; // ぃ ぉ
    if (KANJI_RE.test(b)) return 'kanji';
    if (DASH_RE.test(b)) return 'dash';
    return 'no_product';
  }

  function unusableMessage(ch, reason) {
    switch (reason) {
      case 'small_missing':
        return '小さい「' + ch + '」のワッペンはありません';
      case 'kanji':
        return '漢字（' + ch + '）は使えません。ひらがなで入れてください';
      case 'dash':
        return '「' + ch + '」は使えません。のばす音は「ー」で入れてください';
      case 'lone_mark':
        return '「' + ch + '」だけでは使えません。濁点・半濁点は文字のすぐあとに付けてください';
      case 'mark_conflict':
        return '「' + ch + '」のように濁点と半濁点を両方は付けられません';
      case 'compat':
        return '「' + ch + '」は使えません。ふつうの文字で入れてください';
      case 'daku_missing':
        return '濁点・半濁点のワッペン（濁音セット）が見つかりません';
      default:
        return '「' + ch + '」のワッペンはありません';
    }
  }

  /** 濁音セットの必要数 = max(ceil(濁点/2), 半濁点) */
  function dakuSetsNeeded(dakuten, handakuten) {
    var d = Math.ceil((dakuten || 0) / DAKU_PER_SET);
    var h = Math.ceil((handakuten || 0) / HANDAKU_PER_SET);
    return Math.max(d, h, 0);
  }

  function formatYen(n) {
    return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * 名前を解析する。
   * opts: { maxChars: 20, shippingYen: 360 }
   * 返り値（主なもの）:
   *  ok          … カートに入れてよいか
   *  name        … カートの「お名前」に使う値（cleanName）
   *  tiles       … 名前の順のワッペン（表示用）[{ch, key, marks, product, status}]
   *  lines       … 商品ごとの枚数（同じ文字はまとめる）[{key, label, product, quantity, lineYen, kind}]
   *  letterCount … 文字ワッペンの枚数（濁音セットを含まない）
   *  dakuSets    … 濁音セットの数
   *  patchCount  … カートに入る数量の合計（文字ワッペン＋濁音セット）
   *  countLabel  … 表示用「ワッペン 3枚＋濁音セット1」（濁音が無いときは「ワッペン 3枚」）
   *  dakuten / handakuten / subtotal / shipping / total
   *  unusable    … 使えない文字 [{char, reason, message}]
   *  soldOut     … 在庫切れ・在庫不足 [{char, title, reason, remaining, needed, message}]
   *  messages    … 画面に出す理由（上の2つ＋長すぎ）
   */
  function parseName(input, catalog, opts) {
    opts = opts || {};
    catalog = catalog || { chars: {}, daku: null };
    var maxChars = opts.maxChars > 0 ? opts.maxChars : DEFAULT_MAX_CHARS;
    var shippingYen = typeof opts.shippingYen === 'number' && opts.shippingYen >= 0 ? opts.shippingYen : DEFAULT_SHIPPING_YEN;

    var units = splitUnits(input);

    var res = {
      ok: false,
      empty: units.length === 0,
      tooLong: units.length > maxChars,
      maxChars: maxChars,
      charCount: units.length,
      name: cleanName(input),
      normalized: '',
      tiles: [],
      lines: [],
      dakuten: 0,
      handakuten: 0,
      dakuSets: 0,
      letterCount: 0,
      patchCount: 0,
      countLabel: '',
      subtotal: 0,
      shipping: 0,
      total: 0,
      unusable: [],
      soldOut: [],
      messages: []
    };

    var lineMap = {};
    var lineOrder = [];
    var seenUnusable = {};

    function countMark(m) {
      if (m === MARK_DAKU) res.dakuten += 1;
      else res.handakuten += 1;
    }

    units.forEach(function (u) {
      var ch = displayOf(u);
      res.normalized += ch;
      var tile = { ch: ch, key: null, marks: u.marks.slice(), product: null, status: 'ok', lone: !!u.lone, tilt: tiltFor(ch) };

      var product = null;
      var usesMarks = u.marks.length > 0;
      var blocked = u.lone || u.markConflict || u.foreign || u.compat;
      if (!blocked) {
        // 「が」単体の商品があればそれを使う（いまは無い）
        if (usesMarks && catalog.chars[ch] && Array.from(ch).length === 1) {
          product = catalog.chars[ch];
          usesMarks = false;
          tile.marks = [];
        } else {
          product = catalog.chars[u.base] || null;
        }
      }

      if (!product) {
        tile.status = 'unusable';
        var reason = unusableReason(u);
        if (!seenUnusable[ch]) {
          seenUnusable[ch] = true;
          res.unusable.push({ char: ch, reason: reason, message: unusableMessage(ch, reason) });
        }
        res.tiles.push(tile);
        return;
      }

      if (usesMarks) u.marks.forEach(countMark);
      tile.key = product.key;
      tile.product = product;
      if (!lineMap[product.key]) {
        lineMap[product.key] = { key: product.key, label: product.key, product: product, quantity: 0, lineYen: 0, kind: 'char' };
        lineOrder.push(product.key);
      }
      lineMap[product.key].quantity += 1;
      res.tiles.push(tile);
    });

    res.dakuSets = dakuSetsNeeded(res.dakuten, res.handakuten);
    lineOrder.forEach(function (k) {
      res.lines.push(lineMap[k]);
    });
    if (res.dakuSets > 0) {
      if (catalog.daku) {
        res.lines.push({ key: '濁音', label: DAKU_SET_LABEL, product: catalog.daku, quantity: res.dakuSets, lineYen: 0, kind: 'daku' });
      } else {
        res.unusable.push({ char: DAKU_SPACING, reason: 'daku_missing', message: unusableMessage(DAKU_SPACING, 'daku_missing') });
      }
    }

    // 在庫
    res.lines.forEach(function (l) {
      var label = l.kind === 'daku' ? '濁音セット（゛゜）' : '「' + l.key + '」';
      if (!l.product.available) {
        res.soldOut.push({ char: l.key, title: l.product.title, reason: 'soldout', remaining: 0, needed: l.quantity, message: label + 'は売り切れです' });
        markTiles(l.key, 'soldout');
      } else if (l.product.q != null && l.quantity > l.product.q) {
        res.soldOut.push({
          char: l.key,
          title: l.product.title,
          reason: 'short',
          remaining: l.product.q,
          needed: l.quantity,
          message: label + 'は残り' + l.product.q + '枚です（' + l.quantity + '枚必要）'
        });
        markTiles(l.key, 'soldout');
      }
    });

    function markTiles(key, status) {
      res.tiles.forEach(function (t) {
        if (key === '濁音' ? t.marks.length > 0 && t.product : t.key === key) {
          if (t.status === 'ok') t.status = status;
        }
      });
    }

    // 金額・枚数
    res.lines.forEach(function (l) {
      l.lineYen = l.quantity * l.product.priceYen;
      res.subtotal += l.lineYen;
      res.patchCount += l.quantity;
      if (l.kind === 'char') res.letterCount += l.quantity;
    });
    res.countLabel = 'ワッペン ' + res.letterCount + '枚' + (res.dakuSets > 0 && catalog.daku ? '＋濁音セット' + res.dakuSets : '');
    res.shipping = res.lines.length ? shippingYen : 0;
    res.total = res.subtotal + res.shipping;

    // 理由
    if (res.tooLong) res.messages.push(maxChars + '文字までにしてください（いま' + res.charCount + '文字）');
    res.unusable.forEach(function (x) {
      res.messages.push(x.message);
    });
    res.soldOut.forEach(function (x) {
      res.messages.push(x.message);
    });

    res.ok = !res.empty && !res.tooLong && res.unusable.length === 0 && res.soldOut.length === 0 && res.lines.length > 0;
    return res;
  }

  /** /cart/add.js に送る items。各行に「お名前」を付ける（出荷時の取り違え防止）。 */
  function cartItems(result) {
    if (!result || !result.lines) return [];
    return result.lines.map(function (l) {
      var id = Number(l.product.variantId);
      return { id: isFinite(id) ? id : l.product.variantId, quantity: l.quantity, properties: { 'お名前': result.name } };
    });
  }

  /**
   * 文字ごとの傾き（度）。-3〜3 度を 0.5 度刻みで、文字だけから決める
   * （同じ文字はいつも同じ角度＝打ち直しても、並べ直しても、ぶれない）。
   */
  function tiltFor(ch) {
    var s = String(ch == null ? '' : ch);
    var h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000003;
    return ((h * 7) % 13) / 2 - 3;
  }

  /**
   * 前の並びと今の並びを比べ、新しく足された（入れ替わった）タイルの範囲 [start, end) を返す。
   * 先頭と末尾の一致を除いた真ん中が「新しい」。末尾に1文字足せばその1枚だけ、消しただけなら無し。
   */
  function newTileRange(prev, next) {
    prev = prev || [];
    next = next || [];
    var max = Math.min(prev.length, next.length);
    var p = 0;
    while (p < max && prev[p] === next[p]) p++;
    var s = 0;
    while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
    return { start: p, end: next.length - s };
  }

  /** カート投入に失敗したあと、投入前後の item_count から何が起きたかを決める。 */
  function judgeAfterFailure(before, after, requested) {
    if (before == null || after == null) return 'unknown';
    var added = after - before;
    if (added <= 0) return 'none';
    if (added >= requested) return 'all';
    return 'partial';
  }

  // ---------------------------------------------------------------
  // 画面（ブラウザでだけ使う）
  // ---------------------------------------------------------------

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function readProducts(container) {
    var s = container.querySelector('script[data-nb-products]');
    if (!s) return [];
    try {
      var data = JSON.parse(s.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function intAttr(node, name, fallback) {
    var v = parseInt(node.getAttribute(name), 10);
    return isFinite(v) ? v : fallback;
  }

  function routesRoot() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof r === 'string' && r ? r : '/';
  }

  /**
   * paginate で2ページ以上になったとき（1ページの上限が 50 に丸められた・商品が 250 件を超えた等）、
   * 残りのページを Section Rendering API（?section_id=…&page=N）で取る。
   */
  function loadRemainingPages(rootEl, firstProducts) {
    var pages = intAttr(rootEl, 'data-nb-pages', 1);
    var current = intAttr(rootEl, 'data-nb-current-page', 1);
    var sectionId = rootEl.getAttribute('data-nb-section-id');
    if (pages <= 1 || !sectionId || typeof fetch !== 'function') return Promise.resolve({ products: firstProducts, failed: false });
    var jobs = [];
    for (var p = 1; p <= Math.min(pages, 10); p++) {
      if (p === current) continue;
      jobs.push(fetchPage(sectionId, p));
    }
    return Promise.all(jobs).then(function (lists) {
      var all = firstProducts.slice();
      var failed = false;
      lists.forEach(function (l) {
        if (l === null) failed = true;
        else all = all.concat(l);
      });
      return { products: all, failed: failed };
    });
  }

  function fetchPage(sectionId, page) {
    var url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return readProducts(doc);
      })
      .catch(function () {
        return null;
      });
  }

  /** いまのカートの item_count（数量の合計）。取れなければ null。 */
  function fetchCartCount() {
    return fetch(routesRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (c) {
        return c && typeof c.item_count === 'number' ? c.item_count : null;
      })
      .catch(function () {
        return null;
      });
  }

  function mount(rootEl) {
    if (!rootEl || rootEl.__nbMounted) return;
    rootEl.__nbMounted = true;
    rootEl.setAttribute('data-nb-mounted', 'true');

    var input = rootEl.querySelector('[data-nb-input]');
    var tilesEl = rootEl.querySelector('[data-nb-tiles]');
    var emptyEl = rootEl.querySelector('[data-nb-empty]');
    var dakuEl = rootEl.querySelector('[data-nb-daku]');
    var countEl = rootEl.querySelector('[data-nb-count]');
    var subtotalEl = rootEl.querySelector('[data-nb-subtotal]');
    var totalEl = rootEl.querySelector('[data-nb-total]');
    var msgEl = rootEl.querySelector('[data-nb-messages]');
    var button = rootEl.querySelector('[data-nb-submit]');
    var errorEl = rootEl.querySelector('[data-nb-error]');
    if (!input || !button) return;

    // ボタンの文字は最初の1回だけ覚える（再 mount で「カートに入れています…」を覚えないように）
    var buttonLabel = button.getAttribute('data-nb-label') || String(button.textContent || '').trim();
    button.setAttribute('data-nb-label', buttonLabel);
    var opts = {
      maxChars: intAttr(rootEl, 'data-nb-max', DEFAULT_MAX_CHARS),
      shippingYen: intAttr(rootEl, 'data-nb-shipping', DEFAULT_SHIPPING_YEN)
    };
    var state = { catalog: null, loading: true, incomplete: false, busy: false, composing: false, result: null, prevChars: [] };

    var first = readProducts(rootEl);
    state.catalog = buildCatalog(first);
    publishCounts(first.length);

    loadRemainingPages(rootEl, first).then(function (r) {
      state.catalog = buildCatalog(r.products);
      state.incomplete = r.failed;
      state.loading = false;
      publishCounts(r.products.length);
      update();
    });

    function publishCounts(n) {
      rootEl.setAttribute('data-nb-loaded', String(n));
      rootEl.setAttribute('data-nb-mapped', String(Object.keys(state.catalog.chars).length));
      rootEl.setAttribute('data-nb-has-daku', state.catalog.daku ? 'true' : 'false');
    }

    input.addEventListener('compositionstart', function () {
      state.composing = true;
    });
    input.addEventListener('compositionend', function () {
      state.composing = false;
      update();
    });
    input.addEventListener('input', function (e) {
      if (state.composing || (e && e.isComposing)) return;
      update();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.isComposing) e.preventDefault();
    });

    // カートへ移ったあと「戻る」で戻ってきたとき（bfcache から復帰）、送信中のまま固まらないようにする
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', function (e) {
        if (!e || !e.persisted) return;
        state.busy = false;
        state.composing = false;
        setError('');
        update();
      });
    }

    button.addEventListener('click', function () {
      if (state.busy || state.loading) return;
      // Android の日本語入力などで、確定前（compositionend 前）に押された場合も
      // いまの入力欄の値で判定し直す（1回目の空振りを防ぐ）
      state.composing = false;
      update();
      var r = state.result;
      if (!r || !r.ok) {
        if (r && r.empty && typeof input.focus === 'function') input.focus();
        return;
      }
      submit(r);
    });

    function submit(r) {
      state.busy = true;
      setError('');
      render(r);
      var items = cartItems(r);
      var requested = 0;
      items.forEach(function (it) {
        requested += it.quantity;
      });
      var before = null;
      fetchCartCount()
        .then(function (n) {
          before = n;
          return fetch(routesRoot() + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (res) {
          return res
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { res: res, body: body };
            });
        })
        .then(function (x) {
          if (x.res.ok) {
            goCart();
            return;
          }
          var why = (x.body && (x.body.description || x.body.message)) || 'カートに入れられませんでした（' + x.res.status + '）';
          var shopError = new Error(why);
          shopError.nbShop = true;
          throw shopError;
        })
        .catch(function (err) {
          // 一括投入の一部だけ入ることがあるかは未確定なので、投入前後の数を比べて確かめる
          return fetchCartCount().then(function (after) {
            var judged = judgeAfterFailure(before, after, requested);
            if (judged === 'all') {
              goCart();
              return;
            }
            state.busy = false;
            // お店（Shopify）からの理由はそのまま出す。通信エラー等は日本語の決まり文句にする
            var why = err && err.nbShop ? err.message : 'カートに入れられませんでした。通信状態を確かめて、もう一度お試しください';
            if (judged === 'partial') why = '一部だけカートに入りました。カートを確かめてください（' + why + '）';
            setError(why);
            render(parseName(input.value, state.catalog, opts));
          });
        });
    }

    function goCart() {
      window.location.href = routesRoot() + 'cart';
    }

    function setError(text) {
      if (!errorEl) return;
      errorEl.textContent = '';
      if (!text) {
        errorEl.hidden = true;
        return;
      }
      errorEl.hidden = false;
      errorEl.appendChild(el('span', null, text + '　'));
      var a = el('a', 'nb-link', 'カートを見る');
      a.href = routesRoot() + 'cart';
      errorEl.appendChild(a);
    }

    function update() {
      if (state.busy) return;
      state.result = parseName(input.value, state.catalog, opts);
      render(state.result);
    }

    function render(r) {
      // ワッペンのならび（夜空に、少しずつ傾けた写真のカードとして並べる）
      if (tilesEl) {
        var chars = r.tiles.map(function (t) {
          return t.ch;
        });
        // 新しく足された文字だけ「ぽん」と出す（動きは CSS。prefers-reduced-motion では止める）
        var fresh = newTileRange(state.prevChars, chars);
        state.prevChars = chars;
        tilesEl.textContent = '';
        r.tiles.forEach(function (t, i) {
          var cls = 'nb-tile' + (t.status !== 'ok' ? ' nb-tile--ng' : '') + (i >= fresh.start && i < fresh.end ? ' nb-tile--new' : '');
          var li = el('li', cls);
          li.style.transform = 'rotate(' + t.tilt + 'deg)';
          // まとめて入った新しい文字は1枚ずつ時間差で跳ねる（何枚目か＝--nb-i・動きは name-builder.css の nb-pop）
          if (i >= fresh.start && i < fresh.end && li.style.setProperty) li.style.setProperty('--nb-i', String(i - fresh.start));
          var inner = el('span', 'nb-tile__inner');
          if (t.product && t.product.image) {
            var img = el('img', 'nb-tile__img');
            img.src = t.product.image;
            img.alt = t.ch;
            img.width = 100;
            img.height = 100;
            img.loading = 'eager'; // お客様が打った文字の絵はすぐ見せる（遅延読み込みだと表示が遅れた・2026-10-04）
            inner.appendChild(img);
          } else {
            inner.appendChild(el('span', 'nb-tile__char', t.ch));
          }
          li.appendChild(inner);
          if (t.product && t.marks.length) {
            var markChar = t.marks.indexOf(MARK_HANDAKU) >= 0 ? HANDAKU_SPACING : DAKU_SPACING;
            var mark = el('span', 'nb-tile__mark', markChar);
            mark.setAttribute('aria-hidden', 'true');
            li.appendChild(mark);
            var tileImg = li.querySelector('img');
            if (tileImg) tileImg.alt = t.ch + '（' + t.product.key + '＋' + markChar + '）';
          }
          if (t.status === 'unusable') li.appendChild(el('span', 'nb-tile__flag', 'なし'));
          if (t.status === 'soldout') li.appendChild(el('span', 'nb-tile__flag', '在庫なし'));
          tilesEl.appendChild(li);
        });
      }
      if (emptyEl) emptyEl.hidden = !r.empty;

      // 濁音セット
      if (dakuEl) {
        var line = null;
        r.lines.forEach(function (l) {
          if (l.kind === 'daku') line = l;
        });
        dakuEl.textContent = '';
        dakuEl.hidden = !line;
        if (line) {
          if (line.product.image) {
            var di = el('img', 'nb-daku__img');
            di.src = line.product.image;
            di.alt = '';
            di.width = 100;
            di.height = 100;
            di.loading = 'eager';
            dakuEl.appendChild(di);
          }
          dakuEl.appendChild(el('span', 'nb-daku__text', DAKU_SET_LABEL + ' ×' + line.quantity));
        }
      }

      // 金額
      if (countEl) countEl.textContent = r.countLabel;
      if (subtotalEl) subtotalEl.textContent = formatYen(r.subtotal) + '円（税込）';
      if (totalEl) totalEl.textContent = r.lines.length ? formatYen(r.total) + '円' : '―';

      // 理由
      var reasons = r.messages.slice();
      if (state.loading) reasons.push('商品データを読み込んでいます…');
      if (state.incomplete) reasons.push('商品データの一部を読み込めませんでした。ページを再読み込みしてください');
      if (msgEl) {
        msgEl.textContent = '';
        reasons.forEach(function (m) {
          msgEl.appendChild(el('li', null, m));
        });
        msgEl.hidden = reasons.length === 0;
      }

      // ボタン：押せない状態は aria-disabled で見せ、disabled にするのは送信中だけ
      // （disabled だと、日本語入力の確定前に押されたクリックが届かず空振りするため）
      var can = r.ok && !state.loading && !state.busy;
      button.disabled = state.busy;
      button.setAttribute('aria-disabled', can ? 'false' : 'true');
      button.textContent = state.busy ? 'カートに入れています…' : buttonLabel;
      rootEl.setAttribute('aria-busy', state.busy ? 'true' : 'false');
    }

    update();
  }

  function autoMount() {
    if (typeof document === 'undefined') return;
    var run = function () {
      var list = document.querySelectorAll('[data-nb-root]');
      for (var i = 0; i < list.length; i++) mount(list[i]);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
    // テーマエディタでセクションが描き直されたとき
    document.addEventListener('shopify:section:load', run);
  }

  return {
    DEFAULT_MAX_CHARS: DEFAULT_MAX_CHARS,
    DEFAULT_SHIPPING_YEN: DEFAULT_SHIPPING_YEN,
    DAKU_SET_LABEL: DAKU_SET_LABEL,
    normalizeKey: normalizeKey,
    splitUnits: splitUnits,
    cleanName: cleanName,
    buildCatalog: buildCatalog,
    dakuSetsNeeded: dakuSetsNeeded,
    parseName: parseName,
    cartItems: cartItems,
    judgeAfterFailure: judgeAfterFailure,
    tiltFor: tiltFor,
    newTileRange: newTileRange,
    formatYen: formatYen,
    mount: mount,
    autoMount: autoMount
  };
});
